// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { IconProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { partNumbersStyle } from "@vanmeter/App/ComponentExtensions/ProductPartNumbersExtensions";
import { QtyNumbersStyles } from "@vanmeter/App/Components/Product/QtyNumbers";
import { sharedProductDescriptionStyles } from "@vanmeter/App/SharedStyles/ProductSharedStyles";
import { ProductAvailabilityStyles } from "@vanmeter/Overrides/Components/ProductAvailability";
import { ProductDescriptionStyles } from "@vanmeter/Overrides/Components/ProductDescription";
import { ProductPartNumbersStyles } from "@vanmeter/Overrides/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@vanmeter/Overrides/Components/ProductPrice";
import { CART_BREAKPOINT, fontConfig } from "@vanmeter/XDL/constants";
import X from "@vanmeter/XDL/Icons/IconClose";
import AddToList from "@vanmeter/XDL/Icons/IconList";
import { color, createLinkIconStyles, xScreen } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    qtyLabel?: TypographyProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    unitOfMeasure?: TypographyProps;
    productPartNumbers?: ProductPartNumbersStyles;
    qtyNumbersStyles?: QtyNumbersStyles;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    removeCartLineIcon?: IconProps;
    addToListLink?: LinkPresentationProps;
    productAvailability?: ProductAvailabilityStyles;
    cartLineNotes?: CartLineNotesStyles;
}

const cartLineSharedStyles: CartLineSharedStyles = {
    productPrice: {
        priceWrapper: {
            css: css`
                flex-direction: column;
                gap: 4px;
            `,
        },
        priceLabelText: {
            ...fontConfig.xSmallSingleLine,
            weight: fontConfig.weight.regular,
            css: css`
                padding: 0;
                margin-bottom: 4px;
                display: none;
                ${xScreen("lte", CART_BREAKPOINT)} {
                    display: block;
                }
            `,
        },
        price: {
            priceText: {
                ...fontConfig.regularSingleLine,
                fontFamily: fontConfig.fontFamily,
                weight: fontConfig.weight.bold,
                color: "text",
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    ...fontConfig.smallSingleLine,
                    weight: fontConfig.weight.regular,
                    css: css`
                        white-space: nowrap;
                    `,
                },
            },
        },
        quoteMessage: {
            text: {
                ...fontConfig.regularSingleLine,
                color: "common.ui6",
            },
        },
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            heading: {
                ...fontConfig.xSmallSingleLine,
                weight: fontConfig.weight.regular,
                transform: "capitalize",
                css: css`
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    ${xScreen("lte", CART_BREAKPOINT)} {
                        display: block;
                    }
                `,
            },
            text: {
                ...fontConfig.regularSingleLine,
                fontFamily: fontConfig.fontFamily,
                weight: fontConfig.weight.bold,
                css: css`
                    text-align: start;
                    display: flex;
                `,
            },
        },
    },
    qtyLabel: {
        as: "label",
        ...fontConfig.xSmallSingleLine,
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 4px;
            display: none;
            ${xScreen("lte", CART_BREAKPOINT)} {
                display: block;
            }
        `,
    },
    unitOfMeasure: {
        ...fontConfig.smallSingleLine,
    },
    costCodeLabelText: {
        ...partNumbersStyle.label,
    },
    costCodeValueText: {
        ...partNumbersStyle.value,
    },
    productDescription: {
        ...sharedProductDescriptionStyles({ includeMinHeight: false }),
        productDetailSubDescription: {
            ...sharedProductDescriptionStyles({ sizeVariants: "small", lines: 2, includeMinHeight: false })
                .productDetailDescription,
        },
        descriptionWrapper: {
            css: css`
                margin-bottom: 0;
            `,
        },
    },
    configurationOptionText: {
        ...partNumbersStyle.value,
    },
    configurationOptionLabel: {
        ...partNumbersStyle.label,
    },
    promotionNameLabel: {
        ...partNumbersStyle.label,
    },
    promotionNameText: {
        ...partNumbersStyle.value,
    },
    removeCartLineIcon: {
        src: X,
        width: 12,
        height: 16,
        color: "common.ui5",
        css: css`
            &:hover {
                color: ${color("common.ui15")};
            }
        `,
    },
    addToListLink: {
        ...createLinkIconStyles({
            icon: AddToList,
            sizeVariant: "xsmall",
            iconSize: { width: 12 },
            linkStyles: {
                css: css`
                    white-space: nowrap;
                `,
            },
        }),
    },
    productAvailability: {
        container: {
            css: css`
                ${xScreen("lte", CART_BREAKPOINT)} {
                    flex-direction: column;
                    align-items: flex-start;
                }
            `,
        },
    },
};

export default cartLineSharedStyles;
